import React, { useMemo } from 'react';

import { Layout } from '@components/admin/Layout';
import { CompleteMemberForm } from '@components/admin/members/MembershipFormDetails/CompleteMemberForm';
import { InfoManagement } from '@components/admin/members/MembershipFormDetails/InfoManagement';
import { strings as membershipFormStrings } from '@data/membershipForm';
import { useCompanyChecklist } from '@hooks/queries/dashboard/useCompanyChecklist';
import { useCompanyDetail } from '@hooks/queries/dashboard/useCompanyDetail';
import { useCompanyPrincipal } from '@hooks/queries/dashboard/useCompanyPrincipal';
import { useSearch } from '@hooks/useSearch';
import { MembershipFormFields } from '@models/MembershipForm';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { getBreadcrumb, PageMeta, url } from '@utils/pages';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

const strings = createStrings({
  ja: {
    title: '詳細',
    companyInfo: `客側${membershipFormStrings.companyInfo}`,
    principalInfo: `客側${membershipFormStrings.principalInfo}`,
    checklist: membershipFormStrings.checklist,
    infoManagement: '管理情報',
    edit: '編集',
    id: '申し込み番号',
    createdAt: '申し込み時間',
    status: '未対応',
    admissionDate: '入会時間',
    assignee: 'CS担当者',
    becomeAssignee: '担当者になる',
    evaluation: '会員総合評価',
    memberLegacyNumber: '会員番号',
    export: 'エクスポート',
    createEvaluationSheet: '評価シートを作成',
    inputLegacyMemberNumber: '会員番号を入力',
    inputLegacyDecisionNumber: '稟議番号を入力',
    evaluationSheetK01: '評価シートK01',
    delegationForm: '通関委任状',
    evaluationOptions: () => [
      {
        value: 'A',
        label: 'A. 取引十分可',
      },
      {
        value: 'B',
        label: 'B. 取引可',
      },
      {
        value: 'C',
        label: 'C. 取引再検討',
      },
      {
        value: 'D',
        label: 'D. 取引不可',
      },
    ],
    evaluationGroup: '総合評価',
    save: '保存',
    defaultTransactionConditions: '普通',
    proposedTransactionConditions: '相談したい',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexInline,
    flex: 1,
    padding: '1.5rem 2rem',
    overflow: 'hidden',
  },
  leftColumn: {
    padding: '1.5rem 2rem',
    ...common.flexColumn,
    flex: 1,
    overflowY: 'scroll',
  },
  rightColumn: {
    padding: '1.5rem 2rem',
    ...common.flexColumn,
    width: 360,
    overflowY: 'scroll',
  },
  checklistTextDisplay: {
    padding: '0.5rem 0.5rem',
  },
  formContainer: {
    ...common.flexColumn,
    marginBottom: '1rem',
  },
  managementContainer: {
    ...common.flexColumn,
    border: '1px solid #DDDDDD',
  },
  managementCell: {
    ...common.flexColumn,
    padding: '10px 22px',
    '&:not(:last-child)': {
      borderBottom: '1px solid #DDDDDD',
    },
  },
  keyValueRow: {
    ...common.flexInline,
    ...common.middleText,
    color: theme.palette.grey['700'],
    padding: '0.5rem 0',
  },
  managementTitle: {
    ...common.normalText,
    color: theme.palette.grey['800'],
    fontWeight: 500,
    marginBottom: '0.5rem',
  },
  managementButton: {
    alignSelf: 'center',
    width: 160,
    margin: '0.5rem 0',
  },
  formButton: {
    width: 70,
    margin: '1.5rem 1rem',
  },
  checklistForm: {
    padding: '0.5rem 0.5rem',
  },
  alertDot: {
    height: 10,
    width: 10,
    backgroundColor: theme.palette.error.main,
    marginLeft: 20,
    borderRadius: '50%',
    transform: 'translateY(-1px)',
  },
}));

const searchType = t.type({
  id: h.number().cast(),
  creating: h.omittable(h.boolean().cast()),
});

export const MembershipFormDetails: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search, updateSearch } = useSearch(searchType);

  const company = useCompanyDetail(search.id);
  const principal = useCompanyPrincipal(search.id);
  const checklist = useCompanyChecklist(search.id);

  const breadcrumb = useMemo(
    () => [
      ...getBreadcrumb(url('/admin/members/MembershipForms')),
      String(search.id).padStart(9, '0'),
    ],
    [search.id],
  );

  return (
    <Layout
      breadcrumb={breadcrumb}
      loading={company.isLoading || principal.isLoading || checklist.isLoading}
      title={strings.title}
    >
      {() => {
        const companyFormValues = MembershipFormFields.shared.mapCompanyToCompanyForm(
          company.data!,
        );

        const principalFormValues = MembershipFormFields.shared.mapCompanyPrincipalToPrincipalForm(
          principal.data!,
        );

        const checklistFormValues = MembershipFormFields.shared.mapChecklistToChecklistForm(
          checklist.data!,
        );

        return (
          <div className={classes.container}>
            <div className={classes.leftColumn}>
              <CompleteMemberForm
                initialValues={{
                  ...companyFormValues,
                  ...principalFormValues,
                  ...checklistFormValues,
                }}
                onFinish={() => updateSearch({ ...search, creating: false })}
              />
            </div>
            <div className={classes.rightColumn}>
              <InfoManagement id={search.id} />
            </div>
          </div>
        );
      }}
    </Layout>
  );
};

export const meta: PageMeta = {
  title: strings.title,
  listed: false,
};
